import { useEffect, useState } from 'react';
import { fetchTemplate } from '../api/dataApi';
import { queryClient } from './queryClient';
import type { Channel, Template } from '../types';

// Fully hydrates the templates a channel actually references (its
// `templateIds`, which are template nicknames). The store's `templates`
// list is only a fixed 20-item global fetch (see dataApi.fetchTemplates /
// DEFAULT_PAGE_SIZE), so a channel whose template is older / not owned /
// past that window isn't in it - and any lookup that scans `templates`
// (signal medallion accent colour, chart series colours) silently falls
// back to grey. This fetches the channel's own templates by nickname,
// routed through queryClient so MessagesScreen and ChartScreen share one
// GET /template/{n} per template instead of firing it twice.
//
// Same shape as MessagesScreen's original inline hydration - returns [] for
// a channel with no templateIds, and leaves the previous result in place
// (never flashes to []) while a new channel's templates load.
export function useChannelTemplates(channel: Channel | undefined): Template[] {
  const [hydrated, setHydrated] = useState<Template[]>([]);

  // A stable primitive dep - the templateIds array is a fresh reference on
  // every store update.
  const idsKey = channel?.templateIds?.join(',') ?? '';

  useEffect(() => {
    let cancelled = false;
    const ids = idsKey ? idsKey.split(',') : [];
    if (ids.length === 0) {
      setHydrated([]);
      return;
    }
    Promise.all(
      ids.map((nickname) =>
        queryClient
          .query({
            queryKey: ['channelTemplate', nickname],
            queryFn: () => fetchTemplate(nickname),
          })
          .catch(() => null)
      )
    ).then((results) => {
      if (cancelled) return;
      setHydrated(results.filter((t): t is Template => t !== null && t.signals.length > 0));
    });
    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  return hydrated;
}
